import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { userAPI, walletAPI, apiUtils } from '../api';
import Nav from './nav';
import { useNavigate } from 'react-router-dom';

const WalletApp = () => {
  const [account, setAccount] = useState('Account 1');
  const [balance, setBalance] = useState('$0.00');
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [activeTab, setActiveTab] = useState('wallet');
  const [livePrices, setLivePrices] = useState({});
  const [selectedCoin, setSelectedCoin] = useState(null);
  const [tradeAmount, setTradeAmount] = useState('');
  const [tradeType, setTradeType] = useState('buy');
  const navigate = useNavigate();

  // Enhanced coin data with live trading data
  const [coins, setCoins] = useState([
    { 
      symbol: 'BTC', 
      name: 'Bitcoin', 
      image: '/images/coins/btc.jpg', 
      price: '$0.000',
      change: '+2.5%',
      changeType: 'positive',
      chartData: [65, 59, 80, 81, 56, 55, 40],
      volume: '$45.2B',
      marketCap: '$1.2T'
    },
    { 
      symbol: 'ETH', 
      name: 'Ethereum', 
      image: '/images/coins/eth.jpg', 
      price: '$0.000',
      change: '+1.8%',
      changeType: 'positive',
      chartData: [45, 39, 60, 71, 46, 55, 30],
      volume: '$18.7B',
      marketCap: '$450B'
    },
    { 
      symbol: 'BDX', 
      name: 'Beldex', 
      image: '/images/coins/bdx.png', 
      price: '$0.000',
      change: '-0.5%',
      changeType: 'negative',
      chartData: [30, 25, 40, 35, 50, 45, 60],
      volume: '$12.5M',
      marketCap: '$280M'
    },
    { 
      symbol: 'TRX', 
      name: 'Tron', 
      image: '/images/coins/trx.png', 
      price: '$0.000',
      change: '+3.2%',
      changeType: 'positive',
      chartData: [55, 60, 65, 70, 75, 70, 65],
      volume: '$850M',
      marketCap: '$9.8B'
    },
    { 
      symbol: 'XRP', 
      name: 'Ripple', 
      image: '/images/coins/xrp.png', 
      price: '$0.000',
      change: '-1.2%',
      changeType: 'negative',
      chartData: [40, 45, 50, 48, 52, 50, 48],
      volume: '$1.2B',
      marketCap: '$35B'
    },
    { 
      symbol: 'DOGE', 
      name: 'Dogecoin', 
      image: '/images/coins/doge.png', 
      price: '$0.000',
      change: '+5.7%',
      changeType: 'positive',
      chartData: [25, 30, 28, 35, 40, 38, 42],
      volume: '$680M',
      marketCap: '$12.5B'
    },
    { 
      symbol: 'BNB', 
      name: 'Binance Coin', 
      image: '/images/coins/bnb.jpg', 
      price: '$0.000',
      change: '+0.8%',
      changeType: 'positive',
      chartData: [70, 72, 75, 78, 80, 82, 85],
      volume: '$900M',
      marketCap: '$65B'
    }
  ]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userEmail = localStorage.getItem('userEmail');
    
    if (!token || !userEmail) {
      navigate('/login');
      return;
    }

    fetchUserData();
    startLivePriceUpdates();
  }, [navigate]);

  const fetchUserData = async () => {
    try {
      setLoading(true);
      
      const profileResponse = await userAPI.getProfile();
      setUser(profileResponse.data);

      const walletResponse = await walletAPI.getWallet();
      setWallet(walletResponse.data);
      setBalance(apiUtils.formatCurrency(walletResponse.data.availableBalance || 0));

    } catch (error) {
      console.error('Error fetching user data:', error);
      const errorInfo = apiUtils.handleAPIError(error);
      if (errorInfo.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('userEmail');
        localStorage.removeItem('user');
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const startLivePriceUpdates = () => {
    // Simulate live price updates
    const interval = setInterval(() => {
      const updatedPrices = {};
      coins.forEach(coin => {
        const randomChange = (Math.random() - 0.5) * 10;
        const currentPrice = parseFloat(coin.price.replace('$', '')) || 1000;
        const newPrice = currentPrice * (1 + randomChange / 100);
        updatedPrices[coin.symbol] = {
          price: `$${newPrice.toFixed(2)}`,
          change: `${randomChange > 0 ? '+' : ''}${randomChange.toFixed(2)}%`,
          changeType: randomChange > 0 ? 'positive' : 'negative'
        };
      });
      setLivePrices(updatedPrices);
    }, 3000);

    return () => clearInterval(interval);
  };

  const validateInput = (input) => {
    if (typeof input !== 'string') return '';
    return input.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
  };

  const handleAccountSelect = (selectedAccount) => {
    setAccount(validateInput(selectedAccount));
    setShowAccountModal(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('user');
    localStorage.removeItem('refreshToken');
    navigate('/login');
  };

  const handleTrade = (coin) => {
    setSelectedCoin(coin);
    setTradeAmount('');
  };

  const executeTrade = () => {
    if (!tradeAmount || !selectedCoin) return;
    navigate('/user/deposit');
    
    // Here you would integrate with your trading API
    // alert(`${tradeType.toUpperCase()} order for ${tradeAmount} ${selectedCoin.symbol} placed successfully!`);
    // setSelectedCoin(null);
    // setTradeAmount('');
  };

  const getCurrentPrice = (coin) => {
    return livePrices[coin.symbol]?.price || coin.price;
  };

  const getCurrentChange = (coin) => {
    return livePrices[coin.symbol]?.change || coin.change;
  };

  const getChangeType = (coin) => {
    return livePrices[coin.symbol]?.changeType || coin.changeType;
  };

  const renderMiniChart = (data) => {
    const max = Math.max(...data);
    const min = Math.min(...data);
    
    return (
      <div className="w-16 h-8 flex items-end space-x-0.5">
        {data.map((value, index) => (
          <div
            key={index}
            className="flex-1 bg-gradient-to-t from-blue-500 to-blue-300 rounded-sm"
            style={{ height: `${((value - min) / (max - min)) * 100}%` }}
          />
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-300">Loading your wallet...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 text-white">
      {/* Header */}
      <div className="bg-gray-800/50 backdrop-blur-lg border-b border-gray-700 sticky top-0 z-40">
        <div className="container mx-auto px-4 py-3">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                <i className="fas fa-wallet text-white"></i>
              </div>
              <div>
                <h1 className="text-xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                  CryptoWallet
                </h1>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <button 
                onClick={() => setShowAccountModal(true)}
                className="flex items-center space-x-2 bg-gray-700/50 hover:bg-gray-700 px-3 py-2 rounded-lg transition-all duration-200"
              >
                <div className="w-8 h-8 bg-gradient-to-r from-green-400 to-blue-500 rounded-full flex items-center justify-center text-xs font-bold">
                  {user?.name?.charAt(0) || 'U'}
                </div>
                <span className="hidden sm:block">{user?.name || 'User'}</span>
                <i className="fas fa-chevron-down text-xs"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-6 pb-24">
        {/* Tab Navigation */}
        <div className="flex space-x-1 bg-gray-800/50 rounded-xl p-1 mb-6 backdrop-blur-sm">
          {[
            { id: 'wallet', icon: 'fas fa-wallet', label: 'Wallet' },
            { id: 'trading', icon: 'fas fa-chart-line', label: 'Trading' },
            { id: 'coins', icon: 'fas fa-coins', label: 'Market' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center space-x-2 py-3 px-4 rounded-lg transition-all duration-200 ${
                activeTab === tab.id 
                  ? 'bg-gradient-to-r from-blue-500 to-purple-600 shadow-lg shadow-blue-500/25' 
                  : 'hover:bg-gray-700/50'
              }`}
            >
              <i className={tab.icon}></i>
              <span className="font-medium">{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Wallet Tab */}
        {activeTab === 'wallet' && (
          <div className="space-y-6">
            {/* Wallet Card */}
            <div className="bg-gradient-to-br from-blue-600/20 to-purple-600/20 border border-gray-700/50 rounded-2xl p-6 backdrop-blur-sm">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-gray-300 text-sm">Total Balance</p>
                  <h2 className="text-4xl font-bold mt-2 bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                    {wallet?.totalBalance || '$0.00'}
                  </h2>
                  <p className="text-green-400 text-sm mt-2">
                    <i className="fas fa-arrow-up mr-1"></i>
                    {wallet?.totalProfit || '+0.00%'} Today
                  </p>
                </div>
                <div className="text-right">
                  <button 
                    onClick={() => setShowAccountModal(true)}
                    className="bg-gray-700/50 hover:bg-gray-700 px-4 py-2 rounded-lg transition-colors"
                  >
                    <i className="fas fa-cog"></i>
                  </button>
                </div>
              </div>

              {/* Wallet Stats */}
              {wallet && (
                <div className="grid grid-cols-2 gap-4 mt-6">
                  <div className="bg-gray-800/50 rounded-xl p-4 backdrop-blur-sm">
                    <p className="text-gray-400 text-sm">Available</p>
                    <p className="font-semibold text-lg">{wallet.availableBalance || '0.00'}</p>
                  </div>
                  <div className="bg-gray-800/50 rounded-xl p-4 backdrop-blur-sm">
                    <p className="text-gray-400 text-sm">Total Profit</p>
                    <p className="font-semibold text-lg text-green-400">{wallet.totalProfit || '0.00'}</p>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="grid grid-cols-4 gap-3 mt-6">
                {[
                  { icon: 'fas fa-arrow-down', text: 'Deposit', color: 'from-green-500 to-emerald-500', link: '/user/deposit' },
                  { icon: 'fas fa-arrow-up', text: 'Withdraw', color: 'from-red-500 to-pink-500', link: '/user/withdraw' },
                  { icon: 'fas fa-exchange-alt', text: 'Swap', color: 'from-blue-500 to-cyan-500', link: '#' },
                  { icon: 'fas fa-gift', text: 'Earn', color: 'from-purple-500 to-indigo-500', link: '/user/package' }
                ].map((item, index) => (
                  <a
                    key={index}
                    href={item.link}
                    className="flex flex-col items-center p-3 rounded-xl bg-gray-800/50 hover:bg-gray-700/50 transition-all duration-200 backdrop-blur-sm"
                  >
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-r ${item.color} flex items-center justify-center shadow-lg`}>
                      <i className={`${item.icon} text-white`}></i>
                    </div>
                    <span className="text-xs mt-2 text-gray-300">{item.text}</span>
                  </a>
                ))}
              </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-gray-800/50 rounded-xl p-4 text-center backdrop-blur-sm">
                <div className="w-10 h-10 bg-gradient-to-r from-orange-500 to-red-500 rounded-xl flex items-center justify-center mx-auto mb-2">
                  <i className="fas fa-fire text-white text-sm"></i>
                </div>
                <p className="text-gray-400 text-xs">Active Trades</p>
                <p className="font-semibold">12</p>
              </div>
              <div className="bg-gray-800/50 rounded-xl p-4 text-center backdrop-blur-sm">
                <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-teal-500 rounded-xl flex items-center justify-center mx-auto mb-2">
                  <i className="fas fa-trophy text-white text-sm"></i>
                </div>
                <p className="text-gray-400 text-xs">Portfolio Gain</p>
                <p className="font-semibold text-green-400">+8.2%</p>
              </div>
              <div className="bg-gray-800/50 rounded-xl p-4 text-center backdrop-blur-sm">
                <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center mx-auto mb-2">
                  <i className="fas fa-bell text-white text-sm"></i>
                </div>
                <p className="text-gray-400 text-xs">Alerts</p>
                <p className="font-semibold">3</p>
              </div>
            </div>
          </div>
        )}

        {/* Trading Tab */}
        {activeTab === 'trading' && (
          <div className="space-y-6">
            {/* Live Trading Widget */}
            <div className="bg-gray-800/50 border border-gray-700/50 rounded-2xl p-6 backdrop-blur-sm">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-semibold">Live Trading</h3>
                <div className="flex space-x-2">
                  <button className="bg-blue-500 hover:bg-blue-600 px-4 py-2 rounded-lg text-sm font-medium transition-colors">
                    <i className="fas fa-bolt mr-2"></i>
                    Quick Trade
                  </button>
                </div>
              </div>

              {/* Trading Pairs */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
                {coins.slice(0, 4).map((coin, index) => (
                  <div
                    key={index}
                    onClick={() => handleTrade(coin)}
                    className={`bg-gray-700/50 hover:bg-gray-700 rounded-xl p-4 cursor-pointer transition-all duration-200 backdrop-blur-sm ${
                      selectedCoin?.symbol === coin.symbol ? 'ring-2 ring-blue-500' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        <img 
                          src={coin.image} 
                          alt={coin.name}
                          className="w-6 h-6 rounded-full"
                        />
                        <span className="font-medium text-sm">{coin.symbol}</span>
                      </div>
                      <span className={`text-xs font-medium ${
                        getChangeType(coin) === 'positive' ? 'text-green-400' : 'text-red-400'
                      }`}>
                        {getCurrentChange(coin)}
                      </span>
                    </div>
                    <div className="text-lg font-bold">{getCurrentPrice(coin)}</div>
                    {renderMiniChart(coin.chartData)}
                  </div>
                ))}
              </div>

              {/* Trading Interface */}
              {selectedCoin && (
                <div className="bg-gray-700/30 rounded-xl p-4 backdrop-blur-sm">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <img 
                        src={selectedCoin.image} 
                        alt={selectedCoin.name}
                        className="w-8 h-8 rounded-full"
                      />
                      <div>
                        <h4 className="font-semibold">{selectedCoin.symbol}</h4>
                        <p className="text-gray-400 text-sm">{selectedCoin.name}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold">{getCurrentPrice(selectedCoin)}</div>
                      <div className={`text-sm ${
                        getChangeType(selectedCoin) === 'positive' ? 'text-green-400' : 'text-red-400'
                      }`}>
                        {getCurrentChange(selectedCoin)}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <button
                      onClick={() => setTradeType('buy')}
                      className={`py-3 rounded-lg font-semibold transition-all ${
                        tradeType === 'buy' 
                          ? 'bg-green-500 hover:bg-green-600 shadow-lg shadow-green-500/25' 
                          : 'bg-gray-600 hover:bg-gray-500'
                      }`}
                    >
                      BUY
                    </button>
                    <button
                      onClick={() => setTradeType('sell')}
                      className={`py-3 rounded-lg font-semibold transition-all ${
                        tradeType === 'sell' 
                          ? 'bg-red-500 hover:bg-red-600 shadow-lg shadow-red-500/25' 
                          : 'bg-gray-600 hover:bg-gray-500'
                      }`}
                    >
                      SELL
                    </button>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <label className="text-sm text-gray-400 mb-2 block">Amount</label>
                      <input
                        type="number"
                        value={tradeAmount}
                        onChange={(e) => setTradeAmount(e.target.value)}
                        placeholder="0.00"
                        className="w-full bg-gray-600/50 border border-gray-500 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 backdrop-blur-sm"
                      />
                    </div>
                    <button
                      onClick={executeTrade}
                      disabled={!tradeAmount}
                      className={`w-full py-3 rounded-lg font-semibold transition-all ${
                        tradeType === 'buy'
                          ? 'bg-green-500 hover:bg-green-600 disabled:bg-gray-600'
                          : 'bg-red-500 hover:bg-red-600 disabled:bg-gray-600'
                      } shadow-lg`}
                    >
                      {tradeType === 'buy' ? 'BUY' : 'SELL'} {selectedCoin.symbol}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Recent Trades */}
            <div className="bg-gray-800/50 border border-gray-700/50 rounded-2xl p-6 backdrop-blur-sm">
              <h3 className="text-lg font-semibold mb-4">Recent Trades</h3>
              <div className="space-y-3">
                {[
                  { symbol: 'BTC', type: 'buy', amount: '0.05', price: '$52,450', time: '2 min ago' },
                  { symbol: 'ETH', type: 'sell', amount: '1.2', price: '$3,450', time: '1 hour ago' },
                  { symbol: 'BNB', type: 'buy', amount: '5.0', price: '$580', time: '3 hours ago' }
                ].map((trade, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-700/30 rounded-lg backdrop-blur-sm">
                    <div className="flex items-center space-x-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        trade.type === 'buy' ? 'bg-green-500/20' : 'bg-red-500/20'
                      }`}>
                        <i className={`fas fa-${trade.type === 'buy' ? 'arrow-down' : 'arrow-up'} text-${
                          trade.type === 'buy' ? 'green' : 'red'
                        }-400`}></i>
                      </div>
                      <div>
                        <div className="font-medium">{trade.symbol}</div>
                        <div className="text-gray-400 text-sm">{trade.time}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-medium">{trade.amount}</div>
                      <div className="text-gray-400 text-sm">{trade.price}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Market Tab */}
        {activeTab === 'coins' && (
          <div className="bg-gray-800/50 border border-gray-700/50 rounded-2xl overflow-hidden backdrop-blur-sm">
            <div className="p-6 border-b border-gray-700/50">
              <h3 className="text-lg font-semibold">Market Overview</h3>
              <p className="text-gray-400 text-sm">Real-time cryptocurrency prices</p>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-700/50">
                    <th className="text-left py-4 px-6 text-gray-400 font-medium">Asset</th>
                    <th className="text-right py-4 px-6 text-gray-400 font-medium">Price</th>
                    <th className="text-right py-4 px-6 text-gray-400 font-medium">24h Change</th>
                    <th className="text-right py-4 px-6 text-gray-400 font-medium">Chart</th>
                    <th className="text-right py-4 px-6 text-gray-400 font-medium">Trade</th>
                  </tr>
                </thead>
                <tbody>
                  {coins.map((coin, index) => (
                    <tr key={index} className="border-b border-gray-700/30 hover:bg-gray-700/20 transition-colors">
                      <td className="py-4 px-6">
                        <div className="flex items-center space-x-3">
                          <img 
                            src={coin.image} 
                            alt={coin.name}
                            className="w-8 h-8 rounded-full"
                          />
                          <div>
                            <div className="font-medium">{coin.symbol}</div>
                            <div className="text-gray-400 text-sm">{coin.name}</div>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-6 text-right font-medium">
                        {getCurrentPrice(coin)}
                      </td>
                      <td className="py-4 px-6 text-right">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          getChangeType(coin) === 'positive' 
                            ? 'bg-green-500/20 text-green-400' 
                            : 'bg-red-500/20 text-red-400'
                        }`}>
                          <i className={`fas fa-${getChangeType(coin) === 'positive' ? 'arrow-up' : 'arrow-down'} mr-1`}></i>
                          {getCurrentChange(coin)}
                        </span>
                      </td>
                      <td className="py-4 px-6 text-right">
                        {renderMiniChart(coin.chartData)}
                      </td>
                      <td className="py-4 px-6 text-right">
                        <button
                          onClick={() => handleTrade(coin)}
                          className="bg-blue-500 hover:bg-blue-600 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                        >
                          Trade
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Bottom Navigation */}
      <Nav />

      {/* Account Modal */}
      {showAccountModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 border border-gray-700 rounded-2xl w-full max-w-sm">
            <div className="flex justify-between items-center p-6 border-b border-gray-700">
              <span className="font-semibold text-lg">Account</span>
              <button 
                onClick={() => setShowAccountModal(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <i className="fas fa-times text-xl"></i>
              </button>
            </div>
            
            <div className="p-6">
              <div className="flex items-center space-x-4 mb-6">
                <div className="w-16 h-16 bg-gradient-to-r from-green-400 to-blue-500 rounded-2xl flex items-center justify-center text-xl font-bold">
                  {user?.name?.charAt(0) || 'U'}
                </div>
                <div>
                  <p className="font-semibold text-lg">{user?.name}</p>
                  <p className="text-gray-400 text-sm">{user?.email}</p>
                  <span className="inline-flex items-center px-2 py-1 bg-green-500/20 text-green-400 rounded-full text-xs font-medium mt-1">
                    <i className="fas fa-check-circle mr-1"></i>
                    {user?.kycStatus || 'Verified'}
                  </span>
                </div>
              </div>

              {/* <div className="space-y-3">
                <button className="w-full flex items-center justify-between p-3 bg-gray-700/50 hover:bg-gray-700 rounded-xl transition-colors">
                  <span>Security Settings</span>
                  <i className="fas fa-chevron-right text-gray-400"></i>
                </button>
                <button className="w-full flex items-center justify-between p-3 bg-gray-700/50 hover:bg-gray-700 rounded-xl transition-colors">
                  <span>Transaction History</span>
                  <i className="fas fa-chevron-right text-gray-400"></i>
                </button>
                <button className="w-full flex items-center justify-between p-3 bg-gray-700/50 hover:bg-gray-700 rounded-xl transition-colors">
                  <span>Help & Support</span>
                  <i className="fas fa-chevron-right text-gray-400"></i>
                </button>
              </div> */}


              <div className="space-y-3">
  <a
    href="#"
    className="w-full flex items-center justify-between p-3 bg-gray-700/50 hover:bg-gray-700 rounded-xl transition-colors block"
  >
    <span>Security Settings</span>
    <i className="fas fa-chevron-right text-gray-400"></i>
  </a>

  <a
    href="/user/deposit"
    className="w-full flex items-center justify-between p-3 bg-gray-700/50 hover:bg-gray-700 rounded-xl transition-colors block"
  >
    <span>Transaction History</span>
    <i className="fas fa-chevron-right text-gray-400"></i>
  </a>

<a
  href="https://wa.me/12897923500"
  target="_blank"
  rel="noopener noreferrer"
  className="w-full flex items-center justify-between p-3 bg-gray-700/50 hover:bg-gray-700 rounded-xl transition-colors block"
>
  <span className="flex items-center gap-2">
    <i className="fab fa-whatsapp text-green-500 text-xl"></i> 
    Help & Support
  </span>
  <i className="fas fa-chevron-right text-gray-400"></i>
</a>

<a
  href="https://t.me/Heidi0047"
  target="_blank"
  rel="noopener noreferrer"
  className="w-full flex items-center justify-between p-3 bg-gray-700/50 hover:bg-gray-700 rounded-xl transition-colors block"
>
  <span className="flex items-center gap-2">
    <i className="fab fa-telegram text-blue-400 text-xl"></i>
    Help & Support (Telegram)
  </span>
  <i className="fas fa-chevron-right text-gray-400"></i>
</a>



  {/* <a
    href="#"
    className="w-full flex items-center justify-between p-3 bg-gray-700/50 hover:bg-gray-700 rounded-xl transition-colors block"
  >
    <span>Help & Support</span>
    <i className="fas fa-chevron-right text-gray-400"></i>
  </a> */}
</div>


              <button
                onClick={handleLogout}
                className="w-full bg-gradient-to-r from-red-500 to-pink-600 hover:from-red-600 hover:to-pink-700 text-white font-semibold py-3 px-4 rounded-xl transition-all duration-200 mt-6 shadow-lg shadow-red-500/25"
              >
                <i className="fas fa-sign-out-alt mr-2"></i>
                Sign Out
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Trade Modal */}
      {selectedCoin && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 border border-gray-700 rounded-2xl w-full max-w-md">
            <div className="flex justify-between items-center p-6 border-b border-gray-700">
              <div className="flex items-center space-x-3">
                <img 
                  src={selectedCoin.image} 
                  alt={selectedCoin.name}
                  className="w-8 h-8 rounded-full"
                />
                <div>
                  <span className="font-semibold text-lg">{selectedCoin.symbol}</span>
                  <span className="text-gray-400 ml-2">{selectedCoin.name}</span>
                </div>
              </div>
              <button 
                onClick={() => setSelectedCoin(null)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <i className="fas fa-times text-xl"></i>
              </button>
            </div>
            
            <div className="p-6">
              <div className="text-center mb-6">
                <div className="text-3xl font-bold mb-2">{getCurrentPrice(selectedCoin)}</div>
                <div className={`text-lg ${
                  getChangeType(selectedCoin) === 'positive' ? 'text-green-400' : 'text-red-400'
                }`}>
                  {getCurrentChange(selectedCoin)}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-6">
                <button
                  onClick={() => setTradeType('buy')}
                  className={`py-4 rounded-xl font-semibold text-lg transition-all ${
                    tradeType === 'buy' 
                      ? 'bg-green-500 hover:bg-green-600 shadow-lg shadow-green-500/25' 
                      : 'bg-gray-700 hover:bg-gray-600'
                  }`}
                >
                  BUY
                </button>
                <button
                  onClick={() => setTradeType('sell')}
                  className={`py-4 rounded-xl font-semibold text-lg transition-all ${
                    tradeType === 'sell' 
                      ? 'bg-red-500 hover:bg-red-600 shadow-lg shadow-red-500/25' 
                      : 'bg-gray-700 hover:bg-gray-600'
                  }`}
                >
                  SELL
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="text-sm text-gray-400 mb-2 block">Amount ({selectedCoin.symbol})</label>
                  <input
                    type="number"
                    value={tradeAmount}
                    onChange={(e) => setTradeAmount(e.target.value)}
                    placeholder="0.00"
                    className="w-full bg-gray-700 border border-gray-600 rounded-xl px-4 py-4 text-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div className="bg-gray-700/50 rounded-xl p-4">
                  <div className="flex justify-between text-sm text-gray-400 mb-2">
                    <span>Estimated Cost</span>
                    <span>${(parseFloat(tradeAmount || 0) * parseFloat(getCurrentPrice(selectedCoin).replace('$', ''))).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm text-gray-400">
                    <span>Fee</span>
                    <span>$0.00</span>
                  </div>
                </div>

                <button
                  onClick={executeTrade}
                  disabled={!tradeAmount}
                  className={`w-full py-4 rounded-xl font-semibold text-lg transition-all ${
                    tradeType === 'buy'
                      ? 'bg-green-500 hover:bg-green-600 disabled:bg-gray-600'
                      : 'bg-red-500 hover:bg-red-600 disabled:bg-gray-600'
                  } shadow-lg`}
                >
                  {tradeType === 'buy' ? 'BUY' : 'SELL'} {selectedCoin.symbol}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Include Font Awesome */}
      <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0-beta3/css/all.min.css" />
    </div>
  );
};

export default WalletApp;